import React, { useEffect, useState, useCallback } from "react";
import { driverApi } from "../../services/driverApi";

const PERIODS = [
  { label: "This Week", key: "week" },
  { label: "This Month", key: "month" },
  { label: "Last 30 Days", key: "30d" },
  { label: "Custom", key: "custom" },
];

function getDateRange(periodKey) {
  const today = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (periodKey === "week") {
    const dow = today.getDay(); // 0=Sun
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((dow + 6) % 7));
    return { from: fmt(monday), to: fmt(today) };
  }
  if (periodKey === "month") {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: fmt(first), to: fmt(today) };
  }
  if (periodKey === "30d") {
    const d = new Date(today);
    d.setDate(today.getDate() - 29);
    return { from: fmt(d), to: fmt(today) };
  }
  return null;
}

const STATUS_STYLE = {
  Completed: "bg-green-100 text-green-700",
  Cancelled: "bg-red-100 text-red-600",
  NoShow: "bg-yellow-100 text-yellow-700",
};

function VerificationLetterCard() {
  const [downloading, setDownloading] = useState(false);
  const [dlError, setDlError] = useState(null);

  const handleDownload = async () => {
    setDownloading(true);
    setDlError(null);
    try {
      await driverApi.downloadVerificationLetter();
    } catch (e) {
      setDlError(e.message || "Failed to generate letter.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">Independent Contractor Verification</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Download an official letter confirming your independent contractor status with the company.
            Useful for banks, housing applications, or any institution requiring proof of your working arrangement.
          </p>
          {dlError && (
            <p className="text-xs text-red-600 mt-1">{dlError}</p>
          )}
        </div>
      </div>
      <button
        onClick={handleDownload}
        disabled={downloading}
        className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition"
      >
        {downloading ? (
          <>
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Generating…
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download Verification Letter (PDF)
          </>
        )}
      </button>
    </div>
  );
}

export default function DriverEarnings() {
  const [period, setPeriod] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    let params = {};
    if (period === "custom") {
      if (!customFrom || !customTo) return;
      params = { from: customFrom, to: customTo };
    } else {
      const range = getDateRange(period);
      if (range) params = range;
    }
    setLoading(true);
    setError(null);
    driverApi.getEarnings(params)
      .then(setData)
      .catch((e) => setError(e.message || "Failed to load earnings."))
      .finally(() => setLoading(false));
  }, [period, customFrom, customTo]);

  useEffect(() => {
    if (period !== "custom") load();
  }, [period, load]);

  const fmt$ = (n) =>
    Number(n).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const fmtMiles = (n) => (typeof n === "number" && n > 0 ? `${n.toFixed(1)} mi` : null);

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-900">Earnings</h1>
        <p className="text-sm text-gray-500 mt-0.5">Your trip earnings from the dispatch system.</p>
      </div>

      {/* Period selector */}
      <div className="flex gap-2 flex-wrap mb-4">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
              period === p.key
                ? "bg-blue-600 text-white"
                : "bg-white border border-gray-300 text-gray-600 hover:border-blue-400"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom range inputs */}
      {period === "custom" && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-gray-500 mb-1">To</label>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={load}
              disabled={!customFrom || !customTo}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg disabled:opacity-40 hover:bg-blue-700 transition"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Loading…</div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>
      )}

      {!loading && !error && data && !data.linked && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-sm font-semibold text-blue-900 mb-1">Earnings not yet available</p>
          <p className="text-xs text-blue-600">{data.message}</p>
        </div>
      )}

      {/* Verification letter — always available regardless of link status */}
      {!loading && <div className="mb-4"><VerificationLetterCard /></div>}

      {!loading && !error && data?.linked && (
        <>
          {/* Driver / Cab info */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {data.driver.firstName} {data.driver.lastName}
              </p>
              <p className="text-xs text-gray-500">
                {data.driver.cabNumber ? `Cab #${data.driver.cabNumber}` : "No cab assigned"}
                {data.driver.vehicle ? ` · ${data.driver.vehicle}` : ""}
              </p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs text-gray-400">Period</p>
              <p className="text-xs font-medium text-gray-600">
                {data.summary.from} – {data.summary.to}
              </p>
            </div>
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 mb-1">Total Earnings</p>
              <p className="text-2xl font-bold text-green-600">{fmt$(data.summary.totalRevenue)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 mb-1">Completed Trips</p>
              <p className="text-2xl font-bold text-gray-900">{data.summary.completedTrips}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 mb-1">Avg. Fare</p>
              <p className="text-xl font-bold text-blue-600">{fmt$(data.summary.avgFare)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 mb-1">Cancelled / No-Show</p>
              <p className="text-xl font-bold text-gray-400">
                {data.summary.cancelledTrips + data.summary.noShowTrips}
              </p>
            </div>
          </div>

          {/* Trip list */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-900">Trip History</p>
            </div>

            {data.trips.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-400">No trips in this period.</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {data.trips.map((trip) => (
                  <li key={trip.id} className="px-4 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-gray-400">
                        #{trip.bookingId ?? "—"}
                        {trip.fareStrategy === "flat" && (
                          <span className="ml-1.5 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded text-[10px] font-semibold">Flat rate</span>
                        )}
                        {fmtMiles(trip.meterMiles) && (
                          <span className="ml-1.5 text-gray-400">{fmtMiles(trip.meterMiles)}</span>
                        )}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLE[trip.status] || "bg-gray-100 text-gray-600"}`}>
                        {trip.status}
                      </span>
                    </div>
                    <div className="flex items-end justify-between">
                      <div className="flex-1 min-w-0 pr-3">
                        <p className="text-sm text-gray-700 truncate">{trip.pickupAddress || "—"}</p>
                        {trip.dropoffAddress && (
                          <p className="text-xs text-gray-400 truncate">→ {trip.dropoffAddress}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-0.5">{fmtDate(trip.tripDate)}</p>
                      </div>
                      {trip.status === "Completed" && (
                        <p className="text-base font-bold text-green-600 flex-shrink-0">{fmt$(trip.fare)}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
